import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import * as Yup from 'yup';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import { Form, Formik } from 'formik';
import { Divider, Grid } from '@mui/material';
import DialogActions from '@mui/material/DialogActions';
import _ from 'lodash';

import useNotification from '../../../../../../core/hooks/use_notification';
import { Button, SubmitButton } from '../../../../../common/button';
import { AutocompleteFormik } from '../../../../../common/autocomplete';
import { deleteProductAvailabilityAction, deleteProductAvailabilityState } from '../../../../../../features/reservation/delete_product_availability';
import { getProductsAvailabilityState } from '../../../../../../features/reservation/get_products_availability';
import { parseDateString } from '../../../../../../core/utils/date_utils';
import DatePickerFormik from '../../../../../common/date_picker';
import { MISC_DATE_ISO_DATE_ONLY_FORMAT } from '../../../../../../core/constants';
import { deleteProductAvailabilityNotificationData } from '../../../../../../core/utils/notification_utils';
import { ApiRequestStatusEnum } from '../../../../../../core/enums/common/api';

const DeleteProductAvailabilityFormDialog = ({ refreshCalendar }) => {
    const dispatch = useDispatch();
    const { showNotification } = useNotification();

    const [open, setOpen] = useState(false);

    const getProductsAvailability = useSelector(getProductsAvailabilityState);
    const deleteProductAvailability = useSelector(deleteProductAvailabilityState);

    const mapProductOptions = useCallback((data) => {
        return data?.map(product => ({
            key: product?.productId + Math.random(),
            value: product?.productId,
            label: product?.productName
        }));
    }, []);

    const productOptions = mapProductOptions([...new Map(getProductsAvailability?.data?.map(productsAvailability => productsAvailability?.products)?.flat().map((item) => [item['productId'], item])).values()]) || [];

    const onClose = () => setOpen(false);

    const initialValues = {
        product: '',
        fromDate: '',
        toDate: ''
    };

    const schema = Yup.object().shape({
        product: Yup.object().notRequired(),
        fromDate: Yup.date()
            .transform(parseDateString)
            .nullable()
            .required(),
        toDate: Yup.date()
            .transform(parseDateString)
            .nullable()
            .required()
    });

    const onSubmit = (values) => {
        const productId = _.pick(values, ['product'])?.product?.value || '';
        const fromDate = values?.fromDate?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT);
        const toDate = values?.toDate?.format(MISC_DATE_ISO_DATE_ONLY_FORMAT);

        const form = {
            fromDate,
            toDate,
            productId
        };

        dispatch(deleteProductAvailabilityAction(form))
            .then(response => {
                showNotification(deleteProductAvailabilityNotificationData(response));
                response?.meta?.requestStatus === ApiRequestStatusEnum.FULFILLED && refreshCalendar();
            });
    };

    return (
        <>
            <Button color="error" onClick={ () => setOpen(true) }>
                Usuń produkt
            </Button>

            <Dialog disableRestoreFocus open={ open } onClose={ onClose } fullWidth maxWidth="sm">
                <DialogTitle sx={ { fontWeight: 600, pb: 1 } }>Usuń produkt z kalendarza</DialogTitle>
                <Divider/>

                <DialogContent sx={ { py: 3 } }>
                    <Formik
                        initialValues={ initialValues }
                        validationSchema={ schema }
                        validateOnChange={ false }
                        validateOnBlur={ false }
                        onSubmit={ onSubmit }>
                        { () => (
                            <Form>
                                <Grid container spacing={ 3 }>
                                    <Grid item xs={ 12 }>
                                        <AutocompleteFormik
                                            name="product"
                                            label="Produkt"
                                            options={ productOptions }
                                        />
                                    </Grid>
                                    <Grid item xs={ 12 } sm={ 6 }>
                                        <DatePickerFormik
                                            name="fromDate"
                                            label="Data od"
                                            required
                                            InputLabelProps={ { shrink: true } }
                                        />
                                    </Grid>
                                    <Grid item xs={ 12 } sm={ 6 }>
                                        <DatePickerFormik
                                            name="toDate"
                                            label="Data do"
                                            required
                                            InputLabelProps={ { shrink: true } }
                                        />
                                    </Grid>
                                </Grid>

                                <Divider sx={ { mt: 3, mb: 2, mx: -3 } }/>

                                <DialogActions sx={ { p: 0, gap: 1 } }>
                                    <Button variant="outlined" onClick={ onClose }>Zamknij</Button>
                                    <SubmitButton color="error"
                                                  isLoading={ deleteProductAvailability?.loading }>Usuń</SubmitButton>
                                </DialogActions>
                            </Form>
                        ) }
                    </Formik>
                </DialogContent>
            </Dialog>
        </>
    );
};

export default DeleteProductAvailabilityFormDialog;